/**
 * Ce que le bouton « Créer » propose (décision du 17/10/2026, comme les
 * applications de terrain du marché) : chaque entrée ouvre la page de saisie
 * de son domaine, avec l'écran d'origine comme retour. La liste suit les
 * droits ; au terrain, scanner un QR code passe en tête (`createLead`).
 */
import type { LucideIcon } from 'lucide-react';
import type { Capability, SpecializedModule } from '@myequip/shared';
import { t } from '../core/i18n.js';
import { ICONS } from '../design/icons.js';
import { routeAllowed, type RouteAccess } from './route-types.js';

export interface CreateAction {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Une seule de ces capacités suffit. */
  anyOf?: readonly Capability[];
  module?: SpecializedModule;
}

const PLANNING_EDIT = ['planning.edit_own', 'planning.edit_all'] as const;

/** Par fréquence d'usage : la visite et la tâche d'abord, les achats ensuite. */
const CREATE_ACTIONS: readonly CreateAction[] = [
  {
    to: '/planning/nouvelle-intervention', label: t('nav.creation.intervention'), icon: ICONS.intervention,
    anyOf: ['intervention.create_any', 'intervention.create_own'],
  },
  // Un client qui appelle : la demande se note, puis se planifie (17/10/2026).
  {
    to: '/interventions/demandes/nouvelle', label: t('nav.creation.demande'), icon: ICONS.demandes,
    anyOf: ['intervention.create_any'],
  },
  { to: '/taches/nouvelle', label: t('nav.creation.tache'), icon: ICONS.taches },
  { to: '/clients/nouveau', label: t('nav.creation.client'), icon: ICONS.clients, anyOf: ['customer.manage'] },
  {
    to: '/devis/nouveau', label: t('nav.creation.devis'), icon: ICONS.devis,
    anyOf: ['quote.manage', 'quote.send_within_limits'],
  },
  { to: '/factures/nouvelle', label: t('nav.creation.facture'), icon: ICONS.ventes, anyOf: ['invoice.manage'] },
  { to: '/contrats/nouveau', label: t('nav.creation.contrat'), icon: ICONS.contrats, anyOf: ['contract.manage'] },
  {
    to: '/fournisseurs/factures/nouvelle', label: t('nav.creation.depense'), icon: ICONS.depenses,
    anyOf: ['expense.record', 'expense.manage'],
  },
  {
    to: '/fournisseurs/nouvelle-demande', label: t('nav.creation.achat'), icon: ICONS.achats,
    anyOf: ['purchase.propose'],
  },
  // Un mouvement demande `stock.consume_transfer` à l'API : gérer le stock seul n'y suffit pas.
  { to: '/stock/mouvement', label: t('nav.creation.mouvement'), icon: ICONS.mouvement, anyOf: ['stock.consume_transfer'] },
  // Inventaire d'un dépôt ou d'un véhicule (décision 15) : gestion du stock, comme l'API.
  { to: '/stock/inventaires/nouveau', label: t('nav.creation.inventaire'), icon: ICONS.inventaire, anyOf: ['stock.manage'] },
  // Annonce à l'équipe (25/10/2026) : qui publie (le patron par défaut) ; rubrique coupée, plus personne.
  { to: '/messages/annonces/nouvelle', label: t('nav.creation.annonce'), icon: ICONS.annonce, anyOf: ['announcement.publish'] },
  // Chacun demande ses propres absences (l'API l'accepte de tout compte) : sans droit requis.
  { to: '/absences/nouvelle', label: t('nav.creation.absence'), icon: ICONS.absences },
  { to: '/planning/bloquer-du-temps', label: t('nav.creation.tempsBloque'), icon: ICONS.tempsBloque, anyOf: PLANNING_EDIT },
  // Le plus rare : la reprise d'un fichier d'un autre outil (décision 15 du 19/10/2026).
  { to: '/entreprise/import/nouveau', label: t('nav.creation.import'), icon: ICONS.import, anyOf: ['settings.manage'] },
];

/** Entrées préférées en tête hors terrain : l'intervention, puis la facture, puis la dépense. */
const OFFICE_LEAD_ORDER = ['/planning/nouvelle-intervention', '/factures/nouvelle', '/fournisseurs/factures/nouvelle'] as const;

/**
 * Le geste principal de la feuille « Créer », selon la vue : scanner un QR
 * code au terrain (technicien, ou responsable en vue Terrain) ; au bureau,
 * l'entrée la plus fréquente que les droits permettent (une comptabilité
 * crée une facture ou saisit une dépense, elle ne scanne pas). Le scanner
 * reste proposé parmi les autres entrées. `'scan'` aussi quand rien d'autre
 * n'est permis.
 */
export function createLead(actions: readonly CreateAction[], terrain: boolean): 'scan' | CreateAction {
  if (terrain) return 'scan';
  for (const path of OFFICE_LEAD_ORDER) {
    const found = actions.find((action) => action.to === path);
    if (found) return found;
  }
  return actions[0] ?? 'scan';
}

export function createActionsFor(access: RouteAccess): CreateAction[] {
  return CREATE_ACTIONS.filter((action) => routeAllowed(action, access));
}
